import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useDebounce } from 'react-use';
import cn from 'clsx';

import { Text } from '@bizzabo/design-system';

import useIsMobile from '../../../hooks/useIsMobile';

interface PanelSearchProps {
    placeholder?: string;
    closeSearch: () => void;
    onChange: (searchTerm: string) => void;
}

const PanelSearch: React.FC<PanelSearchProps> = ({ closeSearch, placeholder, onChange }) => {
    const classes = useStyles();
    const [init, setInit] = useState<boolean>(false);
    const [value, setValue] = useState<string>('');
    const isFirstLoad = useRef(true);
    const inputRef = useRef<HTMLInputElement>();
    const isMobile = useIsMobile();

    useEffect(() => {
        setInit(true);
    }, []);

    useEffect(() => {
        if (!isMobile) {
            inputRef.current.focus();
        } else {
            inputRef.current.blur();
        }
    }, [isMobile]);

    const [, cancel] = useDebounce(
        () => {
            if (!isFirstLoad.current) {
                onChange(value);
            }
            isFirstLoad.current = false;
        },
        300,
        [value]
    );

    const closeSearchHandle = () => {
        cancel();
        setValue('');
        onChange('');
        closeSearch();
    };

    return (
        <div className={classes.wrapper}>
            <div className={cn(classes.panelSearch, { slide: init })}>
                <input
                    ref={inputRef}
                    value={value}
                    type="text"
                    onChange={event => setValue(event.target.value)}
                    className={classes.searchInput}
                    placeholder={placeholder}
                />
                <button className={classes.cancelBtn} onClick={closeSearchHandle}>
                    <Text variant="t81" className={classes.cancelText}>
                        Cancel
                    </Text>
                </button>
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        wrapper: {
            width: '100%',
            direction: 'rtl',
        },
        panelSearch: {
            direction: 'ltr',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.1s linear',
            opacity: 0,
            width: 0,
            overflow: 'hidden',
            '&.slide': {
                width: '100%',
                opacity: 1,
            },
            margin: '40px 0',
        },
        searchInput: {
            flexGrow: 1,
            width: 0, // to override default input width
            border: `1px solid ${theme.palette.greys[300]}`,
            borderRadius: 20,
            height: 36,
            padding: '10px 20px',
            marginRight: 10,
            fontSize: 14,
            backgroundColor: theme.palette.greys[100],
            color: theme.palette.greys[900],
            '&:focus': {
                outline: 'none',
            },
            '&::placeholder': {
                color: theme.palette.greys[400],
            },
        },
        cancelBtn: {
            display: 'flex',
            alignItems: 'center',
            fontSize: 20,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            height: 22,
            padding: '0 6px',
        },
        cancelText: {
            color: '#272727',
        },
    }),
    { classNamePrefix: PanelSearch.name }
);

export default PanelSearch;
